import { useState } from 'react'
import toast from 'react-hot-toast'
import {CheckCircle2} from 'lucide-react'

interface FormState {
  name: string
  email: string
  phone: string
  linkedin: string
  message: string
}

const initialState: FormState = {
  name: '',
  email: '',
  phone: '',
  linkedin: '',
  message: '',
}

interface JobApplicationFormProps {
  jobTitle: string
  onClose?: () => void
}

function JobApplicationForm({ jobTitle, onClose }: JobApplicationFormProps) {
  const [form, setForm] = useState<FormState>(initialState)
  const [errors, setErrors] = useState<Partial<FormState>>({})
  const [submitted, setSubmitted] = useState(false)

  const validate = (): boolean => {
    const nextErrors: Partial<FormState> = {}
    if (!form.name.trim())
      nextErrors.name = 'Informe seu nome completo.'
    if (!form.email.trim() || !form.email.includes('@'))
      nextErrors.email = 'Informe um e-mail válido.'
    if (!form.phone.trim())
      nextErrors.phone = 'Informe um telefone de contato.'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {
      toast.error('Por favor, corrija os campos indicados.')
      return
    }
    setSubmitted(true)
    toast.success('Candidatura enviada com sucesso!')
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-cyan-100 bg-cyan-50 p-6 text-center">
        <CheckCircle2 className="h-8 w-8 text-cyan-600" aria-hidden="true" />
        <h4 className="text-sm font-semibold text-slate-900">Candidatura recebida!</h4>
        <p className="max-w-sm text-xs text-slate-600">
          Obrigado pelo interesse na vaga de {jobTitle}, {form.name.split(' ')[0]}. Nossa equipe de RH analisará seu perfil e retornará em breve.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-5" noValidate>
      <p className="text-xs font-semibold uppercase tracking-widest text-cyan-600">Candidatar-se para {jobTitle}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`app-name-${jobTitle}`} className="mb-1 block text-xs font-medium text-slate-700">Nome completo *</label>
          <input
            id={`app-name-${jobTitle}`}
            type="text"
            value={form.name}
            onChange={handleChange('name')}
            aria-invalid={Boolean(errors.name)}
            className={`w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-1 ${errors.name ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:border-cyan-500 focus:ring-cyan-500'}`}
          />
          {errors.name ? <p className="mt-1 text-xs text-red-600">{errors.name}</p> : null}
        </div>
        <div>
          <label htmlFor={`app-email-${jobTitle}`} className="mb-1 block text-xs font-medium text-slate-700">E-mail *</label>
          <input
            id={`app-email-${jobTitle}`}
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            aria-invalid={Boolean(errors.email)}
            className={`w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-1 ${errors.email ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:border-cyan-500 focus:ring-cyan-500'}`}
          />
          {errors.email ? <p className="mt-1 text-xs text-red-600">{errors.email}</p> : null}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`app-phone-${jobTitle}`} className="mb-1 block text-xs font-medium text-slate-700">Telefone *</label>
          <input
            id={`app-phone-${jobTitle}`}
            type="tel"
            value={form.phone}
            onChange={handleChange('phone')}
            aria-invalid={Boolean(errors.phone)}
            className={`w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-1 ${errors.phone ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:border-cyan-500 focus:ring-cyan-500'}`}
          />
          {errors.phone ? <p className="mt-1 text-xs text-red-600">{errors.phone}</p> : null}
        </div>
        <div>
          <label htmlFor={`app-linkedin-${jobTitle}`} className="mb-1 block text-xs font-medium text-slate-700">LinkedIn ou portfólio</label>
          <input
            id={`app-linkedin-${jobTitle}`}
            type="text"
            value={form.linkedin}
            onChange={handleChange('linkedin')}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
          />
        </div>
      </div>
      <div>
        <label htmlFor={`app-message-${jobTitle}`} className="mb-1 block text-xs font-medium text-slate-700">Mensagem (opcional)</label>
        <textarea
          id={`app-message-${jobTitle}`}
          rows={3}
          value={form.message}
          onChange={handleChange('message')}
          className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
        />
      </div>
      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cyan-700"
        >
          Enviar candidatura
        </button>
        {onClose ? (
          <button type="button" onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-700">
            Cancelar
          </button>
        ) : null}
      </div>
    </form>
  )
}

export default JobApplicationForm
